import { useState } from "react";
import { Bell, Instagram, Phone } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { formatDistanceToNow } from "date-fns";
import { ptBR } from "date-fns/locale";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { ScrollArea } from "@/components/ui/scroll-area";
import { MessengerIcon } from "@/components/icons/MessengerIcon";
import { cn } from "@/lib/utils";
import { useUnread } from "@/contexts/UnreadContext";
import type { UnreadConversationPreview } from "@/hooks/useUnreadNotifications";
import { buildConversationInboxUrl } from "@/lib/conversation-inbox-url";

function stripMarkdown(text: string): string {
  return text
    .replace(/```([^`]+)```/g, "$1")
    .replace(/\*\*(.+?)\*\*/g, "$1")
    .replace(/\*([^*]+)\*/g, "$1")
    .replace(/_([^_]+)_/g, "$1")
    .replace(/~([^~]+)~/g, "$1");
}

function ChannelIcon({ channel }: { channel: UnreadConversationPreview["channel"] }) {
  const className = "h-4 w-4 flex-shrink-0";
  if (channel === "instagram") {
    return <Instagram className={cn(className, "text-pink-600")} />;
  }
  if (channel === "messenger") {
    return <MessengerIcon className={cn(className, "text-blue-600")} />;
  }
  return <Phone className={cn(className, "text-green-600")} />;
}

export function UnreadNotificationBadge() {
  const navigate = useNavigate();
  const { unreadCount, unreadPreviews } = useUnread();
  const [open, setOpen] = useState(false);

  const openConversation = (item: UnreadConversationPreview) => {
    setOpen(false);
    // Sinaliza leitura imediatamente para limpar badge/preview otimisticamente;
    // o reset definitivo do unread_count no DB ocorre em Conversations.tsx ao
    // processar o `conversation_id` da URL.
    window.dispatchEvent(
      new CustomEvent("conversation-read", {
        detail: { conversationId: item.conversationId },
      }),
    );
    navigate(
      buildConversationInboxUrl({
        conversationId: item.conversationId,
        channel: item.channel,
        integrationId: item.integrationId,
      }),
    );
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="relative h-8 w-8 sm:h-9 sm:w-9"
          aria-label={`${unreadCount} mensagens não lidas`}
        >
          <Bell className="h-[1.2rem] w-[1.2rem]" />
          {unreadCount > 0 && (
            <span
              className={cn(
                "absolute -top-0.5 -right-0.5 flex items-center justify-center",
                "min-w-[18px] h-[18px] px-1 rounded-full",
                "bg-destructive text-destructive-foreground text-[10px] font-bold",
                "border-2 border-background shadow-sm",
                "animate-pulse"
              )}
            >
              {unreadCount > 99 ? "99+" : unreadCount}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        className="w-80 max-w-[calc(100vw-2rem)] overflow-hidden p-0 sm:w-96"
      >
        <div className="border-b px-4 py-3">
          <p className="text-sm font-semibold">Notificações</p>
          <p className="text-xs text-muted-foreground">
            {unreadCount === 0
              ? "Nenhuma mensagem não lida"
              : `${unreadCount} conversa${unreadCount === 1 ? "" : "s"} não lida${unreadCount === 1 ? "" : "s"}`}
          </p>
        </div>
        {unreadPreviews.length === 0 ? (
          <p className="px-4 py-6 text-center text-sm text-muted-foreground">
            Nenhuma mensagem não lida
          </p>
        ) : (
          <ScrollArea className="max-h-[min(60vh,320px)] w-full [&>[data-radix-scroll-area-viewport]>div]:!block">
            <ul role="list" className="w-full min-w-0 divide-y">
              {unreadPreviews.map((item) => (
                <li key={item.conversationId} className="min-w-0 px-3 py-2.5 hover:bg-muted/40">
                  <div className="flex min-w-0 gap-2.5">
                    <div className="relative shrink-0">
                      <Avatar className="h-9 w-9">
                        {item.leadAvatarUrl ? (
                          <AvatarImage src={item.leadAvatarUrl} alt={item.leadName} />
                        ) : null}
                        <AvatarFallback className="text-xs">
                          {item.leadName.charAt(0).toUpperCase()}
                        </AvatarFallback>
                      </Avatar>
                      <span className="absolute -bottom-0.5 -right-0.5 rounded-full bg-background p-0.5 shadow-sm">
                        <ChannelIcon channel={item.channel} />
                      </span>
                    </div>
                    <div className="min-w-0 flex-1 overflow-hidden">
                      <div className="flex min-w-0 items-start justify-between gap-2">
                        <p
                          className="min-w-0 flex-1 truncate text-sm font-medium leading-tight"
                          title={item.leadName}
                        >
                          {item.leadName}
                        </p>
                        <span className="shrink-0 whitespace-nowrap text-[10px] text-muted-foreground">
                          {formatDistanceToNow(new Date(item.lastMessageAt), {
                            addSuffix: true,
                            locale: ptBR,
                          })}
                        </span>
                      </div>
                      <p className="mt-0.5 line-clamp-2 break-words text-xs text-muted-foreground">
                        {item.isOutbound && (
                          <span className="text-muted-foreground/80">Você: </span>
                        )}
                        {stripMarkdown(item.preview)}
                      </p>
                      <Button
                        variant="link"
                        size="sm"
                        className="h-auto p-0 text-xs"
                        onClick={() => openConversation(item)}
                      >
                        Abrir conversa
                      </Button>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          </ScrollArea>
        )}
        <div className="border-t p-2">
          <Button
            variant="ghost"
            size="sm"
            className="w-full text-xs"
            onClick={() => {
              setOpen(false);
              navigate("/conversations");
            }}
          >
            Ver todas as conversas
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
